import ProductHeroContent from '@blocks/product/product-hero-content'
import ProductHeroGallery from '@blocks/product/product-hero-gallery'
import { SanityShoppingInfo } from '@data/sanity/queries/types/site'
import { LanguageContext } from '@lib/language-context'
import {
  getFallbackVariantFromOptions,
  getFormattedProductTitle,
  getOptionListLabel,
  getVariantFromOptions,
} from '@lib/product'
import { ProductContext } from '@lib/product-context'
import { getProductOptions } from '@lib/product-options'
import { useCallback, useContext, useMemo, useState } from 'react'

interface ProductHeroProps {
  shoppingInfo?: SanityShoppingInfo
  enablePreview?: boolean
  className?: string
}

const ProductHero = ({
  shoppingInfo,
  enablePreview = false,
  className,
}: ProductHeroProps) => {
  const { locale } = useContext(LanguageContext)
  const { product, activeVariant, setActiveVariantId } =
    useContext(ProductContext)

  // Preview variant
  const [previewVariantId, setPreviewVariantId] = useState<number>()
  const previewVariant = useMemo(() => {
    return product?.variants?.find(
      (variant) => variant.variantID === previewVariantId,
    )
  }, [product, previewVariantId])

  // Product options
  const options = useMemo(() => {
    if (!product) {
      return []
    }

    return product.options.filter((option) => option.values.length > 0)
  }, [product])

  // Product option labels
  const productOptionLabels = useMemo(() => {
    return options.map((option) =>
      getOptionListLabel(product?.optionNames ?? [], option),
    )
  }, [options, product])

  // Selected option names
  const [selectedOptionNames, setSelectedOptionNames] = useState(
    // By default only the option in the 1st position is selected
    product?.options
      ?.filter((option) => option.position === 1)
      ?.map((option) => option.name) ?? [],
  )

  // Product option items
  const productOptionsList = useMemo(() => {
    if (!product || !activeVariant) {
      return []
    }

    return options.map((option) => {
      return getProductOptions(
        option,
        activeVariant.options,
        selectedOptionNames,
        product.galleryPhotos ?? [],
        product.optionSettings ?? [],
        product.variants ?? [],
      )
    })
  }, [activeVariant, options, product, selectedOptionNames])

  const isPreorderActive = useMemo(() => {
    if (
      !product ||
      !product.enablePreorder ||
      !product.preorderCountdownTarget ||
      !product.preorderDeliveryDate
    ) {
      return false
    }

    // Check if coundown timer has reached 0
    const date = Date.parse(product.preorderCountdownTarget)
    const now = Date.now()

    return date > now
  }, [product])

  const isAvailabilityCountdownActive = useMemo(() => {
    if (
      !product ||
      !product.enableAvailabilityCountdown ||
      !product.availabilityCountdownTarget
    ) {
      return false
    }

    const date = Date.parse(product.availabilityCountdownTarget)
    const now = Date.now()

    return date > now
  }, [product])

  // Handle selected option name and active variant changes
  const handleSelect = useCallback(
    (optionName: string, optionValue: string) => {
      setSelectedOptionNames((currentSelectedOptionNames) => {
        if (currentSelectedOptionNames.includes(optionName)) {
          return currentSelectedOptionNames
        }

        return [...currentSelectedOptionNames, optionName]
      })

      if (!activeVariant) {
        return
      }

      let newVariant = getVariantFromOptions(
        product?.variants ?? [],
        activeVariant.options,
        optionName,
        optionValue,
      )

      if (!newVariant) {
        newVariant = getFallbackVariantFromOptions(
          product?.variants ?? [],
          optionName,
          optionValue,
        )
      }

      if (newVariant && newVariant.variantID !== activeVariant?.variantID) {
        setActiveVariantId(newVariant.variantID)
      }
    },
    [activeVariant, product, setActiveVariantId],
  )

  // Handle preview variant changes
  const handlePreview = useCallback(
    (optionName: string, optionValue?: string) => {
      if (!enablePreview || !activeVariant) {
        return
      }

      if (!optionValue) {
        setPreviewVariantId(undefined)
        return
      }

      const newVariant = getVariantFromOptions(
        product?.variants ?? [],
        activeVariant.options,
        optionName,
        optionValue,
      )

      if (newVariant) {
        setPreviewVariantId(newVariant.variantID)
      }
    },
    [activeVariant, enablePreview, product, setPreviewVariantId],
  )

  if (!product || !activeVariant) {
    return null
  }

  return (
    <section className={className}>
      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-12">
        <ProductHeroGallery
          photosets={product.galleryPhotos ?? []}
          variant={activeVariant}
          previewVariant={previewVariant}
        />
        <ProductHeroContent
          title={getFormattedProductTitle(locale, product.title)}
          description={product.description}
          shoppingInfo={shoppingInfo}
          product={product}
          variant={activeVariant}
          options={options}
          productPrice={product.price}
          productComparePrice={product.comparePrice}
          isEveryOptionSelected={
            selectedOptionNames.length === product.options.length
          }
          productOptionsList={productOptionsList}
          productOptionLabels={productOptionLabels}
          isPreorderActive={isPreorderActive}
          preorderCountdownTarget={product.preorderCountdownTarget}
          preorderDeliveryDate={product.preorderDeliveryDate}
          isAvailabilityCountdownActive={isAvailabilityCountdownActive}
          availabilityCountdownTarget={product.availabilityCountdownTarget}
          onSelect={handleSelect}
          onPreview={handlePreview}
        />
      </div>
    </section>
  )
}

export default ProductHero
